import instantsearch, { InstantSearch, InstantSearchOptions, UiState } from 'instantsearch.js';
import Typesense, { Client } from 'typesense';
import TypesenseInstantSearchAdapter from 'typesense-instantsearch-adapter';
import { ConfigureWidgetParams } from 'instantsearch.js/es/widgets/configure/configure';
import initAppearAnim from '../../utils/appear-anim';

interface TypesenseParameters {
  appKey: string;
  host: string;
  port: number;
  protocol: string;
  indexName: string;
  searchParams?: string;
}

const ON_FIRST_RENDER = 'on_first_render';
const ON_RENDER_INSTANTSEARCH = 'on_render_instantsearch';
export { ON_FIRST_RENDER, ON_RENDER_INSTANTSEARCH };

export default class BaseTypesenseSearch extends HTMLElement {
  protected searchInstance: null | InstantSearch = null;
  protected searchClient: unknown;
  protected noHitsOnEmptyQuery = false;
  protected indexName = '';
  protected configureParams: ConfigureWidgetParams | null = null;
  protected isFirstRender = false;
  protected paginationContainer: HTMLDivElement | null = null;
  constructor() {
    super();
  }

  animateAppear() {
    const parentElement = document.getElementById(this.dataset.appearAnimContainerId || '') ?? this;
    initAppearAnim(parentElement.id);
    console.log('animateAppear', parentElement.id, this.dataset.appearAnimContainerId);

    /* setTimeout(() => {
      initAppearAnim(parentElement.id);
    }, 100); */
  }

  onRender() {
    const results = this.searchInstance?.renderState[this.indexName]?.hits?.results;
    if (results) {
      const dataEvent = {
        detail: {
          indexname: this.indexName,
          uiState: this.searchInstance?.renderState[this.indexName],
        },
        bubbles: true, // Allows event to bubble up the DOM tree
        composed: true, // Allows event to cross the Shadow DOM boundary
      };
      if (!this.isFirstRender) {
        this.dispatchEvent(new CustomEvent(ON_FIRST_RENDER, dataEvent));
        this.isFirstRender = true;
      }
      this.animateAppear();
      this.dispatchEvent(new CustomEvent(ON_RENDER_INSTANTSEARCH, dataEvent));
    }
  }

  get typesenseClient(): Client | null {
    const { typesenseParameters } = this.dataset;
    try {
      const config: TypesenseParameters = JSON.parse(typesenseParameters || '{}');
      if (this.isValidTypesenseParameters(config)) {
        return new Typesense.Client({
          nodes: [
            { host: config.host, port: config.port, protocol: config.protocol }
          ],
          apiKey: config.appKey,
          timeoutSeconds: 30,
        });
      }
    } catch (e: unknown) {
      console.warn(`Unable to find typesense client: ${e}`);
    }

    return null;
  }

  buildInstantSearch(
    config: TypesenseParameters,
    searchParams = '{ "query_by": "title" }',
    initialUiState?: UiState,
    onStateChange?: InstantSearchOptions['onStateChange'],
  ) {
    try {
      const isAdapter = new TypesenseInstantSearchAdapter({
        server: {
          apiKey: config.appKey,
          nodes: [
            {
              host: config.host,
              port: config.port,
              protocol: config.protocol,
            },
          ],
        },
        additionalSearchParameters: JSON.parse(searchParams),
      });

      this.searchClient = isAdapter.searchClient;

      const debouncedSearchClient = {
        ...isAdapter.searchClient,
        search(requests) {
          const query = requests[0]?.params?.query ?? '';

          if (!query.length ) {
            return Promise.resolve({
              results: requests.map(() => ({
                hits:             [],
                nbHits:           0,
                nbPages:          0,
                page:             0,
                processingTimeMS: 0,
                hitsPerPage:      0,
                exhaustiveNbHits: false,
                query:            '',
                params:           '',
              })),
            });
          }

          return isAdapter.searchClient.search(requests);
        },
      };

      this.indexName = config.indexName;
      this.searchInstance = instantsearch({
        searchClient: this.noHitsOnEmptyQuery ? debouncedSearchClient : isAdapter.searchClient,
        indexName: config.indexName,
        ...(initialUiState && { initialUiState }),
        ...(onStateChange && { onStateChange }),
      });
      this.searchInstance.on('render', this.onRender.bind(this));
    } catch (e) {
      console.warn('Error while initializing instantsearch', e);
    }
  }

  updatePerPage(hitsPerPage: number) {
    if (this.searchInstance) {
      this.searchInstance.helper?.setQueryParameter('hitsPerPage', hitsPerPage);
      this.searchInstance.helper?.search();
    }
  }

  static get observedAttributes() {
    return ['data-configure-params'];
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue) {
      try {
        const oldConf: ConfigureWidgetParams = JSON.parse(oldValue);
        const newConf: ConfigureWidgetParams = JSON.parse(newValue);

        oldConf.hitsPerPage = parseInt(`${oldConf.hitsPerPage}`, 10);
        newConf.hitsPerPage = parseInt(`${newConf.hitsPerPage}`, 10);

        if (oldConf.hitsPerPage !== newConf.hitsPerPage) this.updatePerPage(newConf.hitsPerPage);
      } catch (e: unknown) {
        console.warn(`[Error] attributeChanged attribute '${name}': ${e}`);
      }
    }
  }

  isValidTypesenseParameters(config: TypesenseParameters) {
    const { appKey, host, port, protocol, indexName } = config;

    return Boolean(appKey && host && port && protocol && indexName);
  }

  initSearchInstance(onStateChange?: InstantSearchOptions['onStateChange']) {
    const {
      typesenseParameters,
      searchParams,
      initialState,
    } = this.dataset;
    try {
      const config: TypesenseParameters = JSON.parse(typesenseParameters || '{}');
      const { indexName } = config;

      // console.log('config: ', config, ', searchParams: ', searchParams);
      if (!this.isValidTypesenseParameters(config)) {
        console.warn('Missing required data attributes for Typesense Search block');
        return;
      }

      // pour le widget configure si besoin
      this.configureParams = JSON.parse(this.dataset.configureParams || '{}');

      // Construit l'initialUiState à partir de data-initial-state
      // ex. { thematique: "Stratégie créative" } → refinementList.thematics = ["Stratégie créative"]
      const uiState = JSON.parse(initialState || '{}');

      this.buildInstantSearch(config, searchParams, uiState[indexName] ? uiState : undefined, onStateChange);
      this.paginationContainer = this.querySelector('.search-pagination');
    } catch (e: unknown) {
      console.warn('Error while parsing search parameters', e);
    }

  }
}